import React, { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { fetchProducts } from '@/lib/api';
import { Footer } from '@/components/Footer';
import { TwitterCardsRow } from '@/components/TwitterCardsRow';

// Brand product photography (used inside the photo cards below)
const PHOTO_CHOCOPOPS = 'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/1ob20q2i_Gemini_Generated_Image_fg15j0fg15j0fg15.png';
const PHOTO_CHOCODIGITS = 'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/qi8ii2iu_Copy%20of%20WhatsApp%20Image%202026-04-01%20at%205.59.07%20PM%20%281%29.jpeg';
const PHOTO_CHOCOSNAPS = 'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/ptp39kef_Chocosnap%202.jpg';
// Brand logos (circular badge on each card)
const LOGO_CHOCOPOPS = 'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/2k1jrqk8_3D%20Chocopops%20Logo.svg';
const LOGO_CHOCODIGITS = 'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/dp7s81oo_3D%20Choco%20Digits.svg';
const LOGO_CHOCOSNAPS = 'https://customer-assets.emergentagent.com/job_sweet-snap/artifacts/3aoengmm_ChocoSnaps%20Logo.svg';

const CUSTOMIZE_CARDS = [
  {
    to: '/product/chocopops-pack-of-24/customize',
    tone: 'cyan',
    photo: PHOTO_CHOCOPOPS,
    logo: LOGO_CHOCOPOPS,
    packSize: 24,
    title: '3D Choco Pops',
    body: [
      'Choose any mix of 24 Choco Pops',
      '8 different animals available',
      'No print customisation needed',
    ],
    price: '₹1,189',
    testid: 'customize-pack-card-chocopops-pack-of-24',
  },
  {
    to: '/product/chocosnaps-pack-of-48/customize',
    tone: 'yellow',
    photo: PHOTO_CHOCOSNAPS,
    logo: LOGO_CHOCOSNAPS,
    packSize: 48,
    title: 'Choco Snaps',
    body: [
      'Upload your photo or logo',
      'Printed on every ChocoSnap in the pack',
      'Perfect for gifts and giveaways',
    ],
    price: '₹1,599',
    testid: 'customize-pack-card-chocosnaps-pack-of-48',
  },
  {
    to: '/product/choco-digits-pack-of-20',
    tone: 'pink',
    photo: PHOTO_CHOCODIGITS,
    logo: LOGO_CHOCODIGITS,
    packSize: 20,
    title: '3D Choco Digits',
    body: [
      'Choose any mix of 20 Choco Digits',
      '10 different digits available',
      'Launching this year',
    ],
    price: '₹1,189',
    comingSoon: true,
    ctaLabelOverride: 'Notify me',
    testid: 'customize-pack-card-choco-digits-pack-of-20',
  },
];

const CustomizeLandingPage = () => {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts({})
      .then((xs) => setPacks(xs.filter((p) => p.is_pack && !p.is_mono)))
      .finally(() => setLoading(false));
  }, []);

  // Only show cards whose product slug is actually present in the DB.
  // Match on slug anywhere in the target URL (some cards link to the PDP directly
  // when coming_soon, others go to the /customize flow).
  const visibleCards = CUSTOMIZE_CARDS.filter((c) =>
    packs.some((p) => c.to.includes(`/product/${p.slug}`)),
  );

  return (
    <div data-testid="customize-landing-page">
      {/* ── Simple pink hero (reverted) ── */}
      <section className="bb-block-pink" data-testid="customize-hero-banner">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-10 md:py-14 text-white">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--bb-yellow)] text-[var(--bb-darkblue)] text-xs font-nunito-black uppercase tracking-wider">
            <Sparkles className="w-4 h-4" /> Customise your pack
          </div>
          <h1 className="font-display text-4xl md:text-6xl mt-3 leading-[0.95] text-white">Build your own box.</h1>
          <p className="text-sm md:text-base text-white/95 mt-3 font-nunito-medium max-w-xl">
            Three packs. One quick quiz. Pick a pack, build your mix, add your customisation — and we&apos;ll make it.
          </p>
        </div>
      </section>

      {/* Quiz step bar */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 mt-6">
        <div className="flex items-center gap-2 sm:gap-3 text-xs md:text-sm font-nunito-bold text-[var(--bb-darkblue)]" data-testid="customize-quiz-progress">
          <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-[var(--bb-pink)] text-white">
            <span className="w-5 h-5 rounded-full bg-white text-[var(--bb-pink)] font-nunito-black text-xs flex items-center justify-center">1</span>
            Pick a pack
          </span>
          <div className="flex-1 h-0.5 bg-[var(--bb-darkblue)]/10" />
          <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border-2 border-[var(--bb-darkblue)]/15 text-[var(--bb-darkgrey)]">
            <span className="w-5 h-5 rounded-full bg-[var(--bb-darkblue)]/10 font-nunito-black text-xs flex items-center justify-center">2</span>
            Build &amp; customise
          </span>
          <div className="flex-1 h-0.5 bg-[var(--bb-darkblue)]/10" />
          <span className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border-2 border-[var(--bb-darkblue)]/15 text-[var(--bb-darkgrey)]">
            <span className="w-5 h-5 rounded-full bg-[var(--bb-darkblue)]/10 font-nunito-black text-xs flex items-center justify-center">3</span>
            Review &amp; checkout
          </span>
        </div>
      </section>

      {/* Pack grid (photo quiz cards) — UNCHANGED */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 mt-6 mb-12">
        <h2 className="font-display text-2xl md:text-3xl text-[var(--bb-darkblue)]">Step 1 — Select your favourite to customise</h2>
        {loading ? (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="rounded-[28px] bg-[var(--bb-yellow-soft)] animate-pulse" style={{ aspectRatio: '0.62 / 1' }} />
            ))}
          </div>
        ) : (
          <TwitterCardsRow
            className="mt-4"
            ctaLabel="Build your pack"
            cards={visibleCards}
          />
        )}
      </section>

      <Footer />
    </div>
  );
};

export default CustomizeLandingPage;
